import { useState, useMemo, useCallback } from 'react'
import { useController, useFormState } from 'react-hook-form'
import { useApiQuery } from '../../hooks/useApiQuery.js'

/**
 * Leg Type to Facility Type Mapping
 * Determines which facility types are valid for each leg type
 */
const LEG_PLACE_MAPPING = {
  AtConsignee: ['warehouse'],
  AtShipper: ['warehouse'],
  InGateEmpty: ['ramp', 'container_yard', 'ocean_terminal'],
  InGateLoaded: ['ramp', 'container_yard', 'ocean_terminal'],
  OutGateEmpty: ['ramp', 'container_yard', 'ocean_terminal'],
  OutGateLoaded: ['ramp', 'container_yard', 'ocean_terminal'],
  OutGateBobtail: ['ramp', 'container_yard', 'ocean_terminal'],
  InGateBobtail: ['ramp', 'container_yard', 'ocean_terminal'],
  AtEquipment: ['pool'],
}

/**
 * Custom "Enter an address" option for manual entry fallback
 */
const CUSTOM_MANUAL_ENTRY_OPTION = {
  formatted_address: 'Enter an address manually',
  uid: 'manual-entry',
  isManualEntry: true,
}

/**
 * PlaceSelect Logic Hook
 * Handles place search, filtering, and selection
 */
export const usePlaceSelectLogic = ({
  name,
  control,
  legType,
  marketUid,
  onSelect,
  lookupBy = 'uid',
}) => {
  // React Hook Form integration
  const {
    field,
    fieldState: { error },
  } = useController({ name, control })

  // Get errors from form-level state to catch .refine() validation errors
  // useController only returns direct field errors, not leg-level refinement errors
  const { errors } = useFormState({ control })

  // Navigate to the nested error path for this field
  // Example: name = "legs.0.placeData" → errors.legs[0].placeData
  const nameParts = name.split('.')
  let fieldError = errors
  for (const part of nameParts) {
    fieldError = fieldError?.[part]
  }

  // Use form-level error if it exists, otherwise fall back to field-level
  // Derive hasError from actualError only (not useController's invalid flag)
  // to avoid stale invalid state after form.clearErrors('legs')
  const actualError = fieldError || error
  const hasError = !!actualError

  // Search query state
  const [searchQuery, setSearchQuery] = useState('')

  // Search places via API (debounced by React Query)
  const { data, isLoading } = useApiQuery({
    key: [
      'searchPlaces',
      { query: { searchQuery, market: marketUid }, is_active: true },
    ],
    payload: {
      query: { searchQuery, market: marketUid },
      is_active: true,
    },
    enabled: Boolean(searchQuery),
    staleTime: 5 * 60 * 1000,
  })

  // Filter results by facility type based on leg type
  const filteredResults = useMemo(() => {
    if (!data?.results) return []

    const validTypes = LEG_PLACE_MAPPING[legType] || []

    return data.results.filter((place) => {
      // If place has no types, include it (non-facility place like Google Places result)
      if (!place.types || place.types.length === 0) return true

      // Otherwise, check if facility type matches leg type requirements
      return place.types.some((type) => validTypes.includes(type))
    })
  }, [data?.results, legType])

  // Transform results to react-select option shape ({ value, label }).
  // `lookupBy` selects which field of the place object identifies the option:
  //   - 'uid'      → DrayNow's external UUID (default; what searchPlaces returns first-class)
  //   - 'place_id' → DrayNow's internal numeric id (used by lanes — saved leg has place_id, not uid)
  const reactSelectOptions = useMemo(() => {
    const placeOptions = filteredResults.map((place) => ({
      value: place[lookupBy],
      label: place.formatted_address,
    }))

    // Add currently selected place to options if it's not already in search results
    // This ensures the selected place is always visible even without searching
    const selectedPlaceInResults =
      field.value?.[lookupBy] &&
      filteredResults.some((place) => place[lookupBy] === field.value[lookupBy])

    if (field.value?.[lookupBy] && !selectedPlaceInResults) {
      placeOptions.unshift({
        value: field.value[lookupBy],
        label: field.value.formatted_address || '',
      })
    }

    // Append manual-entry sentinel option
    return [
      ...placeOptions,
      {
        value: 'manual-entry',
        label: CUSTOM_MANUAL_ENTRY_OPTION.formatted_address,
      },
    ]
  }, [filteredResults, field.value, lookupBy])

  // Compute the selected option object react-select needs (or null when cleared).
  const reactSelectValue = useMemo(() => {
    const selectedId = field.value?.[lookupBy]
    if (selectedId === undefined || selectedId === null) return null
    return reactSelectOptions.find((opt) => opt.value === selectedId) ?? null
  }, [reactSelectOptions, field.value, lookupBy])

  // Handle place selection — react-select calls with the selected option (or null on clear)
  const handleChange = useCallback(
    (option) => {
      const selectedValue = option?.value ?? null

      if (selectedValue === null) {
        // Cleared selection — notify the parent so it can reset any sibling
        // fields it populated when the place was originally picked
        // (place_id, formatted_address, address, contact).
        field.onChange(null)
        setSearchQuery('')
        onSelect({ isCleared: true })
        return
      }

      if (selectedValue === 'manual-entry') {
        // User selected "Enter an address manually"
        // This should trigger the parent to show manual entry fields
        field.onChange(null)
        onSelect(CUSTOM_MANUAL_ENTRY_OPTION) // Pass the manual entry option with isManualEntry flag
        return
      }

      // Look up the full place object by the configured identifier
      const selectedPlace = filteredResults.find(
        (place) => place[lookupBy] === selectedValue,
      )

      if (selectedPlace) {
        // Valid place selected - store full object in form
        field.onChange(selectedPlace)
        onSelect(selectedPlace)
      }
    },
    [field, onSelect, filteredResults, lookupBy],
  )

  const [inputValue, setInputValue] = useState('')
  const [menuIsOpen, setMenuIsOpen] = useState(false)

  const handleInputChange = useCallback((newValue, { action }) => {
    if (action === 'input-change') {
      setInputValue(newValue)
      setSearchQuery(newValue)
    } else {
      setInputValue('')
    }
  }, [])

  const handleMenuOpen = useCallback(() => {
    setMenuIsOpen(true)
    if (reactSelectValue) {
      setInputValue(reactSelectValue.label)
    }
  }, [reactSelectValue])

  const handleMenuClose = useCallback(() => {
    setMenuIsOpen(false)
  }, [])

  return {
    reactSelectOptions,
    reactSelectValue,
    isLoading,
    onChange: handleChange,
    onInputChange: handleInputChange,
    onMenuOpen: handleMenuOpen,
    onMenuClose: handleMenuClose,
    inputValue,
    controlShouldRenderValue: !menuIsOpen,
    onBlur: field.onBlur,
    name: field.name,
    selectRef: field.ref,

    // Validation state
    // Uses form-level error if available (catches .refine() errors)
    // Falls back to field-level error from useController
    error: hasError,
    errorMessage: actualError?.message,
  }
}
